import { Trophy } from 'lucide-react';
import { 
  Landscape as LandscapeIcon,
  Hiking as HikingIcon,
  Terrain as TerrainIcon,
  Route as RouteIcon,
  Forest as ForestIcon,
  Park as ParkIcon,
  SportsScore as SportsScoreIcon,
  EmojiEvents as EmojiEventsIcon,
  Explore as ExploreIcon,
  WbSunny as WbSunnyIcon,
  Opacity as OpacityIcon,
  AcUnit as AcUnitIcon,
  Cloud as CloudIcon,
  Air as AirIcon
} from '@mui/icons-material';
import { useState, useRef, useEffect, useCallback } from 'react';

interface AchievementBadgeProps {
  achievement: {
    _id: string;
    name: string;
    description: string;
    icon: string;
    tier: 'bronze' | 'silver' | 'gold' | 'platinum';
    imageUrl?: string;
  };
  size?: 'small' | 'medium' | 'large';
  showTooltip?: boolean;
}

export default function AchievementBadge({ achievement, size = 'medium', showTooltip = true }: AchievementBadgeProps) {
  const [showTooltipState, setShowTooltipState] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState<'top' | 'bottom'>('bottom');
  const badgeRef = useRef<HTMLDivElement>(null);
  const touchTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const [tooltipOffset, setTooltipOffset] = useState(0);
  const [tooltipAlignment, setTooltipAlignment] = useState<'left' | 'center' | 'right'>('center');

  const handleTooltipPosition = useCallback(() => {
    if (badgeRef.current) {
      const rect = badgeRef.current.getBoundingClientRect();
      const containerRect = badgeRef.current.closest('.container')?.getBoundingClientRect() || 
                          badgeRef.current.closest('.bg-white')?.getBoundingClientRect() ||
                          document.documentElement.getBoundingClientRect();
      
      const tooltipHeight = 80; // Approximate height of tooltip
      const tooltipWidth = 192; // w-48 = 12rem = 192px
      const arrowSize = 8; // Size of the arrow (w-2 = 8px)
      
      // Calculate available space
      const spaceBelow = containerRect.bottom - rect.bottom;
      const spaceAbove = rect.top - containerRect.top;
      const spaceRight = containerRect.right - rect.right;
      const spaceLeft = rect.left - containerRect.left;
      
      // Determine vertical position
      const verticalPosition = spaceAbove > spaceBelow ? 'top' : 'bottom';
      setTooltipPosition(verticalPosition);
      
      // Calculate horizontal position
      const badgeCenter = rect.left + (rect.width / 2);
      const tooltipHalfWidth = tooltipWidth / 2;
      
      // Check if tooltip would overflow on either side
      if (badgeCenter - tooltipHalfWidth < containerRect.left) {
        // Tooltip would overflow left edge
        setTooltipAlignment('left');
        setTooltipOffset(0);
      } else if (badgeCenter + tooltipHalfWidth > containerRect.right) {
        // Tooltip would overflow right edge
        setTooltipAlignment('right');
        setTooltipOffset(0);
      } else {
        // Tooltip can be centered
        setTooltipAlignment('center');
        setTooltipOffset(0);
      }
    }
  }, []);

  useEffect(() => {
    if (showTooltipState) {
      handleTooltipPosition();
    }
  }, [showTooltipState, handleTooltipPosition]);

  useEffect(() => {
    if (!showTooltip) return;
    
    const badge = badgeRef.current;
    if (!badge) return;

    const handleTouchStart = (e: TouchEvent) => {
      e.preventDefault();
      handleTooltipPosition();
      setShowTooltipState(true);
    };

    const handleTouchEnd = () => {
      touchTimeoutRef.current = setTimeout(() => {
        setShowTooltipState(false);
      }, 2000);
    };

    badge.addEventListener('touchstart', handleTouchStart, { passive: false });
    badge.addEventListener('touchend', handleTouchEnd);

    return () => {
      badge.removeEventListener('touchstart', handleTouchStart);
      badge.removeEventListener('touchend', handleTouchEnd);
      if (touchTimeoutRef.current) {
        clearTimeout(touchTimeoutRef.current);
      }
    };
  }, [handleTooltipPosition, showTooltip]);

  const getBadgeColor = (tier: string) => {
    switch (tier) {
      case 'bronze':
        return 'bg-amber-600';
      case 'silver':
        return 'bg-gray-400';
      case 'gold':
        return 'bg-yellow-500';
      case 'platinum':
        return 'bg-purple-500';
      default:
        return 'bg-gray-400';
    }
  };

  const getIconSize = () => {
    switch (size) {
      case 'small':
        return { fontSize: 16 };
      case 'large':
        return { fontSize: 24 };
      default:
        return { fontSize: 20 };
    }
  };

  const getBadgeSize = () => {
    switch (size) {
      case 'small':
        return 'w-7 h-7 max-[425px]:w-6 max-[425px]:h-6';
      case 'large':
        return 'w-11 h-11 max-[425px]:w-9 max-[425px]:h-9';
      default:
        return 'w-9 h-9 max-[425px]:w-7 max-[425px]:h-7';
    }
  };

  const getIcon = () => {
    if (achievement.imageUrl) {
      return (
        <img 
          src={achievement.imageUrl} 
          alt={achievement.name}
          loading="lazy"
          className="w-full h-full object-cover rounded-full"
        />
      );
    }

    switch (achievement.icon) {
      case 'hiking':
        return <HikingIcon className="text-white max-[425px]:scale-75" sx={getIconSize()} />;
      case 'terrain':
        return <TerrainIcon className="text-white max-[425px]:scale-75" sx={getIconSize()} />;
      case 'route':
        return <RouteIcon className="text-white max-[425px]:scale-75" sx={getIconSize()} />;
      case 'landscape':
        return <LandscapeIcon className="text-white max-[425px]:scale-75" sx={getIconSize()} />;
      case 'forest':
        return <ForestIcon className="text-white max-[425px]:scale-75" sx={getIconSize()} />;
      case 'park':
        return <ParkIcon className="text-white max-[425px]:scale-75" sx={getIconSize()} />;
      case 'sports':
        return <SportsScoreIcon className="text-white max-[425px]:scale-75" sx={getIconSize()} />;
      case 'trophy':
        return <EmojiEventsIcon className="text-white max-[425px]:scale-75" sx={getIconSize()} />;
      case 'explore':
        return <ExploreIcon className="text-white max-[425px]:scale-75" sx={getIconSize()} />;
      // Weather-related icons
      case 'wb_sunny':
        return <WbSunnyIcon className="text-white max-[425px]:scale-75" sx={getIconSize()} />;
      case 'opacity':
        return <OpacityIcon className="text-white max-[425px]:scale-75" sx={getIconSize()} />;
      case 'ac_unit':
        return <AcUnitIcon className="text-white max-[425px]:scale-75" sx={getIconSize()} />;
      case 'cloud':
        return <CloudIcon className="text-white max-[425px]:scale-75" sx={getIconSize()} />;
      case 'air':
        return <AirIcon className="text-white max-[425px]:scale-75" sx={getIconSize()} />;
      default:
        return <EmojiEventsIcon className="text-white max-[425px]:scale-75" sx={getIconSize()} />;
    }
  };

  const getTooltipPositionClasses = () => {
    const baseClasses = 'absolute z-10 w-48 p-2 text-sm max-[425px]:text-xs text-white bg-gray-900 rounded-lg shadow-lg';
    const verticalClasses = tooltipPosition === 'top' ? 'bottom-full mb-2' : 'top-full mt-2';
    
    let horizontalClasses = '';
    switch (tooltipAlignment) {
      case 'left':
        horizontalClasses = 'left-0';
        break;
      case 'right':
        horizontalClasses = 'right-0';
        break;
      default:
        horizontalClasses = 'left-1/2 -translate-x-1/2';
    }
    
    return `${baseClasses} ${verticalClasses} ${horizontalClasses}`;
  };

  const getArrowPositionClasses = () => {
    const baseClasses = 'absolute w-2 h-2 bg-gray-900 transform rotate-45';
    const verticalClasses = tooltipPosition === 'top' ? '-bottom-1' : '-top-1';
    
    let horizontalClasses = '';
    switch (tooltipAlignment) {
      case 'left':
        horizontalClasses = 'left-4';
        break;
      case 'right':
        horizontalClasses = 'right-4';
        break;
      default:
        horizontalClasses = 'left-1/2 -translate-x-1/2';
    }
    
    return `${baseClasses} ${verticalClasses} ${horizontalClasses}`;
  };

  return (
    <div className="relative" ref={badgeRef}>
      <div
        className={`flex items-center justify-center rounded-full ${achievement.imageUrl ? '' : getBadgeColor(achievement.tier)} ${getBadgeSize()} ${showTooltip ? 'cursor-pointer' : ''}`}
        onMouseEnter={() => {
          if (showTooltip) {
            handleTooltipPosition();
            setShowTooltipState(true);
          }
        }}
        onMouseLeave={() => showTooltip && setShowTooltipState(false)}
      >
        {getIcon()}
      </div>
      {showTooltip && showTooltipState && (
        <div className={getTooltipPositionClasses()}>
          <div className="font-semibold mb-1 max-[425px]:text-xs">{achievement.name}</div>
          <div className="text-gray-300 max-[425px]:text-xs">{achievement.description}</div>
          <div className={getArrowPositionClasses()}></div>
        </div>
      )}
    </div>
  );
} 